// Activation de la double authentification : clé à saisir dans l'application d'authentification, puis premier code.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Smartphone } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { Alerte, Bouton, Champ, useNotifier } from '../../components/ui.tsx';

export function ActivationMfa({ apres }: { apres?: () => void }) {
  const [cle, setCle] = useState<{ cle: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [mdp, setMdp] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const notifier = useNotifier();
  const qc = useQueryClient();

  const preparer = async () => {
    setErreur(null); setEnvoi(true);
    try { setCle(await api.post('/api/auth/mfa/preparer', { motDePasse: mdp })); setMdp(''); } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  const activer = async () => {
    setErreur(null); setEnvoi(true);
    try {
      await api.post('/api/auth/mfa/activer', { code });
      notifier('Double authentification activée.');
      await qc.invalidateQueries({ queryKey: ['moi'] });
      apres?.();
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };

  if (!cle) {
    return (
      <div className="pile">
        <p>À chaque connexion, un code à 6 chiffres affiché par une application d’authentification de votre téléphone vous sera demandé en plus du mot de passe.</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <form className="pile" onSubmit={(e) => { e.preventDefault(); preparer(); }} style={{ maxWidth: 420 }}>
          <Champ libelle="Votre mot de passe (confirmation)" type="password" autoComplete="current-password" value={mdp} onChange={(e) => setMdp(e.target.value)} />
          <div><Bouton type="submit" variante="principal" chargement={envoi} disabled={!mdp} icone={<ShieldCheck aria-hidden />}>Activer la double authentification</Bouton></div>
        </form>
      </div>
    );
  }
  return (
    <div className="pile">
      <ol className="pile" style={{ paddingLeft: 20, margin: 0 }}>
        <li>Ouvrez votre application d’authentification et ajoutez un compte <strong>avec une clé de configuration</strong>.</li>
        <li>Saisissez cette clé (type « basée sur le temps ») :
          <div style={{ marginTop: 8 }}><code className="mono" style={{ fontSize: 'var(--t-l)', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 6,
            display: 'inline-block', overflowWrap: 'anywhere', userSelect: 'all', letterSpacing: '0.08em' }}>{cle.cle.match(/.{1,4}/g)?.join(' ')}</code></div>
          <div style={{ marginTop: 8 }}><a className="btn btn-compact" href={cle.uri}><Smartphone aria-hidden />Ouvrir dans l’application (sur ce téléphone)</a></div>
        </li>
        <li>Entrez le code à 6 chiffres affiché par l’application.</li>
      </ol>
      {erreur && <Alerte ton="danger">{erreur}</Alerte>}
      <form className="rangee" style={{ alignItems: 'flex-end' }} onSubmit={(e) => { e.preventDefault(); activer(); }}>
        <div style={{ width: 200 }}><Champ libelle="Code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} mono value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></div>
        <Bouton type="submit" variante="principal" chargement={envoi} disabled={code.length !== 6}>Confirmer et activer</Bouton>
      </form>
    </div>
  );
}
